import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, matchRoutes } from "react-router-dom";
import { Suspense, useEffect, type ReactElement } from "react";
import { lazyRoute, type LazyRoute } from "@/lib/lazyRoute";
import { getUTMParams, saveLeadInfo, captureAttributionOnLoad } from "@/lib/lead";
import ScrollToTop from "@/components/ScrollToTop";
import { financeTrack, companyTrack } from "./lib/aiReadinessTracks";

const Index = lazyRoute(() => import("./pages/Index"));
const Blog = lazyRoute(() => import("./pages/Blog"));
const BlogArticle = lazyRoute(() => import("./pages/BlogArticle"));
const BlogAuthor = lazyRoute(() => import("./pages/BlogAuthor"));
const BlogCategory = lazyRoute(() => import("./pages/BlogCategory"));
const Portfolio = lazyRoute(() => import("./pages/Portfolio"));
const PortfolioProject = lazyRoute(() => import("./pages/PortfolioProject"));
const PortfolioRoute = lazyRoute(() => import("./pages/PortfolioRoute"));
const NotFound = lazyRoute(() => import("./pages/NotFound"));
const Contact = lazyRoute(() => import("./pages/Contact"));
const Careers = lazyRoute(() => import("./pages/Careers"));
const CareerRole = lazyRoute(() => import("./pages/CareerRole"));
const WriteForUs = lazyRoute(() => import("./pages/WriteForUs"));
const TermsOfService = lazyRoute(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazyRoute(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazyRoute(() => import("./pages/CookiePolicy"));
const AIReadiness = lazyRoute(() => import("./pages/AIReadiness"));
const SaiaProposal = lazyRoute(() => import("./pages/SaiaProposal"));
const SaiaScans = lazyRoute(() => import("./pages/SaiaScans"));
const AIDataExtract = lazyRoute(() => import("./pages/AIDataExtract"));
const AdminLogin = lazyRoute(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazyRoute(() => import("./pages/admin/AdminDashboard"));
const AdminProfileEditor = lazyRoute(() => import("./pages/admin/AdminProfileEditor"));
const PublicVcard = lazyRoute(() => import("./pages/PublicVcard"));
const BrandingGuideline = lazyRoute(() => import("./pages/BrandingGuideline"));
const Reference = lazyRoute(() => import("./pages/Reference"));
const Products = lazyRoute(() => import("./pages/Products"));
const QweaverProMax = lazyRoute(() => import("./pages/QweaverProMax"));
const QweaverVault = lazyRoute(() => import("./pages/QweaverVault"));
const QweaverBrand = lazyRoute(() => import("./pages/QweaverBrand"));
const CoreaiBrand = lazyRoute(() => import("./pages/CoreaiBrand"));
const AiCmsBrand = lazyRoute(() => import("./pages/AiCmsBrand"));
const AiCmsGuide = lazyRoute(() => import("./pages/AiCmsGuide"));
const Tools = lazyRoute(() => import("./pages/Tools"));
const WebsiteStyle = lazyRoute(() => import("./pages/WebsiteStyle"));
const WebsiteStyleIndex = lazyRoute(() => import("./pages/WebsiteStyleIndex"));
const DesignSystemTool = lazyRoute(() => import("./pages/DesignSystem"));
const ToolRoute = lazyRoute(() => import("./pages/ToolRoute"));
const ErpSoftwareSingapore = lazyRoute(() => import("./pages/ErpSoftwareSingapore"));
const ErpConstructionSingapore = lazyRoute(() => import("./pages/ErpConstructionSingapore"));
const IndustryErp = lazyRoute(() => import("./pages/IndustryErp"));
const BusinessSoftware = lazyRoute(() => import("./pages/BusinessSoftware"));
const ErpFinanceSingapore = lazyRoute(() => import("./pages/ErpFinanceSingapore"));
const AiErpSingapore = lazyRoute(() => import("./pages/AiErpSingapore"));
const CustomErpSingapore = lazyRoute(() => import("./pages/CustomErpSingapore"));
const ErpForSmesSingapore = lazyRoute(() => import("./pages/ErpForSmesSingapore"));
const AiWorkflowAutomationSingapore = lazyRoute(() => import("./pages/AiWorkflowAutomationSingapore"));
const QuotationManagementSystemSingapore = lazyRoute(() => import("./pages/QuotationManagementSystemSingapore"));
const EnterpriseResourcePlanningSystem = lazyRoute(() => import("./pages/EnterpriseResourcePlanningSystem"));
const WebDevelopmentSingapore = lazyRoute(() => import("./pages/WebDevelopmentSingapore"));
const PsgGrantSingapore = lazyRoute(() => import("./pages/PsgGrantSingapore"));
const CloudErpSingapore = lazyRoute(() => import("./pages/CloudErpSingapore"));
const InvoicenowSingapore = lazyRoute(() => import("./pages/InvoicenowSingapore"));
const PosSystemSingapore = lazyRoute(() => import("./pages/PosSystemSingapore"));
const InventoryManagementSoftwareSingapore = lazyRoute(() => import("./pages/InventoryManagementSoftwareSingapore"));
const WarehouseManagementSystemSingapore = lazyRoute(() => import("./pages/WarehouseManagementSystemSingapore"));
const Dashboard = lazyRoute(() => import("./pages/dashboard/Dashboard"));
const DocScan = lazyRoute(() => import("./pages/DocScan"));

// One table drives both the <Routes> below and preloadRoute(), so the page a
// URL resolves to is always the page that gets preloaded for it.
const ROUTES: { path: string; element: ReactElement; page?: LazyRoute }[] = [
  { path: "/", element: <Index />, page: Index },
  { path: "/blog", element: <Blog />, page: Blog },
  { path: "/blog/category/:category", element: <BlogCategory />, page: BlogCategory },
  { path: "/blog/author/:author", element: <BlogAuthor />, page: BlogAuthor },
  { path: "/blog/:slug", element: <BlogArticle />, page: BlogArticle },
  { path: "/portfolio", element: <Portfolio />, page: Portfolio },
  // One dispatcher: a segment is either a taxonomy filter or a
  // project slug. See PortfolioRoute for why taxonomy wins.
  { path: "/portfolio/:a", element: <PortfolioRoute />, page: PortfolioRoute },
  { path: "/portfolio/:a/:b", element: <PortfolioRoute />, page: PortfolioRoute },
  { path: "/terms-of-service", element: <TermsOfService />, page: TermsOfService },
  { path: "/terms", element: <TermsOfService />, page: TermsOfService },
  { path: "/tos", element: <TermsOfService />, page: TermsOfService },
  { path: "/privacy-policy", element: <PrivacyPolicy />, page: PrivacyPolicy },
  { path: "/privacy", element: <PrivacyPolicy />, page: PrivacyPolicy },
  { path: "/cookie-policy", element: <CookiePolicy />, page: CookiePolicy },
  { path: "/cookie", element: <CookiePolicy />, page: CookiePolicy },
  { path: "/cookies", element: <CookiePolicy />, page: CookiePolicy },
  { path: "/ai-readiness", element: <AIReadiness track={financeTrack} />, page: AIReadiness },
  { path: "/ai-finance", element: <AIReadiness track={financeTrack} />, page: AIReadiness },
  { path: "/ai-company", element: <AIReadiness track={companyTrack} />, page: AIReadiness },
  { path: "/saia/:code", element: <SaiaProposal />, page: SaiaProposal },
  { path: "/saia-dashboard", element: <SaiaScans />, page: SaiaScans },
  { path: "/dashboard", element: <Dashboard />, page: Dashboard },
  { path: "/d/:code", element: <DocScan />, page: DocScan },
  { path: "/ai-dataextract", element: <AIDataExtract />, page: AIDataExtract },
  { path: "/admin-321/login", element: <AdminLogin />, page: AdminLogin },
  { path: "/admin-321", element: <AdminDashboard />, page: AdminDashboard },
  { path: "/admin-321/profiles/new", element: <AdminProfileEditor />, page: AdminProfileEditor },
  { path: "/admin-321/profiles/:id", element: <AdminProfileEditor />, page: AdminProfileEditor },
  { path: "/p/:slug", element: <PublicVcard />, page: PublicVcard },
  { path: "/branding-guideline", element: <BrandingGuideline />, page: BrandingGuideline },
  { path: "/reference", element: <Reference />, page: Reference },
  { path: "/products", element: <Products />, page: Products },
  { path: "/products/qweaver-pro-max", element: <QweaverProMax />, page: QweaverProMax },
  { path: "/products/qweaver-vault", element: <QweaverVault />, page: QweaverVault },
  { path: "/products/qweaver", element: <QweaverBrand />, page: QweaverBrand },
  { path: "/products/clinicore", element: <CoreaiBrand />, page: CoreaiBrand },
  { path: "/products/ai-cms", element: <AiCmsBrand />, page: AiCmsBrand },
  { path: "/products/ai-cms/:slug", element: <AiCmsGuide />, page: AiCmsGuide },
  { path: "/products/coreai", element: <Navigate to="/products/clinicore" replace /> },
  { path: "/tools", element: <Tools />, page: Tools },
  { path: "/tools/design-system", element: <DesignSystemTool />, page: DesignSystemTool },
  { path: "/tools/website-style", element: <WebsiteStyleIndex />, page: WebsiteStyleIndex },
  { path: "/tools/website-style/:slug", element: <WebsiteStyle />, page: WebsiteStyle },
  // Static /tools/* routes above win over this catch-all: React Router
  // ranks concrete segments ahead of dynamic ones.
  { path: "/tools/:slug", element: <ToolRoute />, page: ToolRoute },
  { path: "/erp-software-singapore", element: <ErpSoftwareSingapore />, page: ErpSoftwareSingapore },
  { path: "/erp-software-singapore/construction", element: <ErpConstructionSingapore />, page: ErpConstructionSingapore },
  { path: "/erp-software-singapore/finance", element: <ErpFinanceSingapore />, page: ErpFinanceSingapore },
  { path: "/erp-software-singapore/:slug", element: <IndustryErp />, page: IndustryErp },
  { path: "/business-software/:slug", element: <BusinessSoftware />, page: BusinessSoftware },
  { path: "/ai-erp-singapore", element: <AiErpSingapore />, page: AiErpSingapore },
  { path: "/custom-erp-singapore", element: <CustomErpSingapore />, page: CustomErpSingapore },
  { path: "/erp-for-smes-singapore", element: <ErpForSmesSingapore />, page: ErpForSmesSingapore },
  { path: "/ai-workflow-automation-singapore", element: <AiWorkflowAutomationSingapore />, page: AiWorkflowAutomationSingapore },
  { path: "/quotation-management-system-singapore", element: <QuotationManagementSystemSingapore />, page: QuotationManagementSystemSingapore },
  { path: "/enterprise-resource-planning-system", element: <EnterpriseResourcePlanningSystem />, page: EnterpriseResourcePlanningSystem },
  { path: "/web-development-singapore", element: <WebDevelopmentSingapore />, page: WebDevelopmentSingapore },
  { path: "/psg-grant-singapore", element: <PsgGrantSingapore />, page: PsgGrantSingapore },
  { path: "/cloud-erp-singapore", element: <CloudErpSingapore />, page: CloudErpSingapore },
  { path: "/invoicenow-singapore", element: <InvoicenowSingapore />, page: InvoicenowSingapore },
  { path: "/pos-system-singapore", element: <PosSystemSingapore />, page: PosSystemSingapore },
  { path: "/inventory-management-software-singapore", element: <InventoryManagementSoftwareSingapore />, page: InventoryManagementSoftwareSingapore },
  { path: "/warehouse-management-system-singapore", element: <WarehouseManagementSystemSingapore />, page: WarehouseManagementSystemSingapore },
  { path: "/careers", element: <Careers />, page: Careers },
  { path: "/careers/:slug", element: <CareerRole />, page: CareerRole },
  { path: "/write-for-us", element: <WriteForUs />, page: WriteForUs },
  { path: "/contact", element: <Contact />, page: Contact },
  // ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE
  { path: "*", element: <NotFound />, page: NotFound },
];

/** Load the page module for a pathname (no-op for unknown paths). */
export function preloadRoute(pathname: string): Promise<void> {
  const match = matchRoutes(ROUTES, pathname)?.[0]?.route as (typeof ROUTES)[number] | undefined;
  return match?.page?.preload() ?? Promise.resolve();
}

const ROUTE_ELEMENTS = ROUTES.map((r) => <Route key={r.path} path={r.path} element={r.element} />);

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    try {
      const utm = getUTMParams();
      const referrer = document.referrer || undefined;
      if (Object.values(utm).some(Boolean) || referrer) {
        saveLeadInfo({ utm, referrer });
      }
      // Persist full ad-level attribution (fbclid / Meta ad ids) for later submit.
      captureAttributionOnLoad();
    } catch {
      // no-op
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        {/* v7_startTransition: navigating to a page whose chunk is still loading
            keeps the current page on screen instead of blanking to the fallback. */}
        <BrowserRouter future={{ v7_startTransition: true }}>
          <ScrollToTop />
          <Suspense fallback={null}>
            <Routes>{ROUTE_ELEMENTS}</Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
